import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLang } from '../../contexts/LangContext';
import { createTask, getStaffMembers } from '../../api/tasksApi';
import { accountsApi, PermissionGroupOption } from '../../api/accountsApi';
import { useAuth } from '../../contexts/AuthContext';
import { CreateTaskRequest, User } from '../../types';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillTableBetter from 'quill-table-better';
import 'quill-table-better/dist/quill-table-better.css';

// Register the table-better module exactly like SendEmail.tsx
Quill.register('modules/table-better', QuillTableBetter, true);

// Additional CSS for proper table rendering
const tableStyles = `
  .ql-editor table {
    border-collapse: collapse;
    table-layout: fixed;
    width: 100%;
    margin: 10px 0;
  }
  
  .ql-editor table td,
  .ql-editor table th {
    border: 1px solid #000;
    padding: 5px 10px;
    min-width: 50px;
    min-height: 20px;
    position: relative;
  }
  
  .ql-editor table th {
    background-color: #f1f3f4;
    font-weight: bold;
  }
  
  .ql-editor .ql-table-better {
    border: 1px solid #ccc;
  }
  
  .ql-editor .ql-table-better td {
    border: 1px solid #ccc;
    padding: 5px;
    min-width: 50px;
    min-height: 20px;
    position: relative;
  }
  
  .ql-editor .ql-table-better .ql-table-better-cell-selected {
    background-color: #b3d9ff;
  }
`;

const AddTask: React.FC = () => {
  const { t } = useLang();
  const { user, isSuperAdmin, isOperator } = useAuth();
  const navigate = useNavigate();
  const quillRef = useRef<ReactQuill>(null);

  const [loading, setLoading] = useState(false);
  const [permissionGroups, setPermissionGroups] = useState<PermissionGroupOption[]>([]);
  const [selectedPermissionGroupId, setSelectedPermissionGroupId] = useState<string>('');
  const [staffMembers, setStaffMembers] = useState<User[]>([]);
  const [formData, setFormData] = useState<CreateTaskRequest>({
    title: '',
    description: '',
    assignedToId: '',
    dueDate: '',
    priority: 'MEDIUM',
  });
  const [files, setFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    // Load permission groups first
    (async () => {
      try {
        // SUPERADMIN may target a specific operator later; default to none here
        const operatorId = isOperator ? user?.id : undefined;
        const groups = await accountsApi.getPermissionGroups('STAFF', operatorId);
        setPermissionGroups(groups);
      } catch (e) {
        console.error('Error loading permission groups:', e);
      }
    })();
  }, [isOperator, user?.id]);

  useEffect(() => {
    // Load staff when group changes
    loadStaffMembers(selectedPermissionGroupId || undefined);
    // Reset selected staff when group changes
    setFormData(prev => ({ ...prev, assignedToId: '' }));
  }, [selectedPermissionGroupId]);

  const loadStaffMembers = async (permissionGroupId?: string) => {
    try {
      const data = await getStaffMembers(permissionGroupId);
      setStaffMembers(data);
    } catch (error) {
      console.error('Error loading staff members:', error);
      toast.error(t('errorLoadingStaff') || 'Error loading staff members');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDescriptionChange = (value: string) => {
    setFormData(prev => ({
      ...prev,
      description: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length + files.length > 2) {
      toast.error(t('maxTwoFiles') || 'Maximum 2 files allowed');
      return;
    }
    setFiles(prev => [...prev, ...selectedFiles]);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    if (droppedFiles.length + files.length > 2) {
      toast.error(t('maxTwoFiles') || 'Maximum 2 files allowed');
      return;
    }
    setFiles(prev => [...prev, ...droppedFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      toast.error(t('titleRequired') || 'Title is required');
      return false;
    }
    if (!formData.description.trim()) {
      toast.error(t('descriptionRequired') || 'Description is required');
      return false;
    }
    if (!formData.assignedToId) {
      toast.error(t('assigneeRequired') || 'Assignee is required');
      return false;
    }
    if (!formData.dueDate) {
      toast.error(t('dueDateRequired') || 'Due date is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await createTask(formData, files);
      toast.success(t('taskCreatedSuccessfully') || 'Task created successfully');
      navigate('/tasks');
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error(t('errorCreatingTask') || 'Error creating task');
    } finally {
      setLoading(false);
    }
  };

  // Quill modules configuration exactly like SendEmail.tsx
  const modules = {
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'font': [] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'script': 'sub'}, { 'script': 'super' }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image'],
        ['blockquote', 'code-block'],
        ['table-better'],
        ['clean']
      ]
    },
    table: false,
    'table-better': {
      language: 'en_US',
      menus: ['column', 'row', 'merge', 'table', 'cell', 'wrap', 'copy', 'delete'],
      toolbarTable: true,
      operationMenu: {
        items: {
          unmergeCells: {
            text: 'Another unmerge cells name'
          }
        },
        color: {
          colors: ['green', 'red', 'yellow', 'blue', 'white'],
          text: 'Background Colors:'
        }
      }
    },
    keyboard: {
      bindings: QuillTableBetter.keyboardBindings
    },
    clipboard: {
      matchVisual: false,
    }
  };

  const formats = [
    'header', 'font', 'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'indent',
    'link', 'image',
    'color', 'background',
    'align', 'script',
    'code-block',
    // Table-better formats
    'table-cell', 'table-cell-block', 'table-header', 'table-list'
  ];

  return (
    <div className="max-w-7xl mx-auto p-6">
      <style dangerouslySetInnerHTML={{ __html: tableStyles }} />
      
      {/* Breadcrumb */}
      <nav className="flex mb-6 text-sm">
        <span className="text-gray-500">🏠</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-gray-500">CÔNG VIỆC</span>
        <span className="mx-2 text-gray-400">/</span>
        <span className="text-cyan-600 font-medium">Thêm công việc</span>
      </nav>

      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Thêm công việc</h1>
        <p className="text-gray-600 mt-1">Tạo công việc mới và phân công cho nhân viên</p>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Assignment */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
            Đối tượng giao việc
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('permissionGroup')}
              </label>
              <select
                value={selectedPermissionGroupId}
                onChange={(e) => setSelectedPermissionGroupId(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
              >
                <option value="">{t('selectPermissionGroup')}</option>
                {permissionGroups.map((g) => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('assignToStaff')} <span className="text-red-500">*</span>
              </label>
              <select
                name="assignedToId"
                value={formData.assignedToId}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
                required
              >
                <option value="">{t('selectStaffPlaceholder')}</option>
                {staffMembers.map((staff) => (
                  <option key={staff.id} value={staff.id}>
                    {staff.name} - {staff.email}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('taskPriority')}</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white"
              >
                <option value="LOW">{t('taskPriorityLow')}</option>
                <option value="MEDIUM">{t('taskPriorityMedium')}</option>
                <option value="HIGH">{t('taskPriorityHigh')}</option>
                <option value="URGENT">{t('taskPriorityUrgent')}</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('dueDate')} <span className="text-red-500">*</span></label>
              <input
                type="datetime-local"
                name="dueDate"
                value={formData.dueDate}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                required
              />
            </div>

            {/* Action Buttons */}
            <div className="flex space-x-4 pt-4">
              <button
                type="button"
                onClick={() => navigate('/tasks')}
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
              >
                {t('cancel')}
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-medium rounded-lg hover:from-cyan-600 hover:to-blue-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('creating') : t('createTaskButton')}
              </button>
            </div>
          </div>
        </div>

        {/* Right Column - Content */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-100">
            Nội dung công việc
          </h2>
          
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tiêu đề <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
                placeholder="Nhập tiêu đề công việc"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mô tả <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <ReactQuill
                  ref={quillRef}
                  theme="snow"
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Nhập mô tả công việc chi tiết..."
                  style={{ 
                    height: '300px',
                    marginBottom: '50px'
                  }}
                  preserveWhitespace
                />
              </div>
              <div className="text-xs text-gray-500 mt-2">
                💡 Mẹo: Sử dụng nút bảng trong thanh công cụ để tạo bảng chuyên nghiệp. Hỗ trợ copy-paste từ Word/Excel
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                File đính kèm (Tối đa 2 files)
              </label>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
                  dragActive ? 'border-cyan-400 bg-cyan-50' : 'border-gray-300 hover:border-cyan-400'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                  id="file-upload"
                  accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <svg className="w-8 h-8 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-sm text-gray-600">Kéo thả file hoặc click để chọn</p>
                  <p className="text-xs text-gray-500 mt-1">PNG, JPG, PDF, DOC, XLS (tối đa 2 files)</p>
                </label>
              </div>

              {/* Selected Files */}
              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <div>
                          <span className="text-sm font-medium text-gray-700">{file.name}</span>
                          <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeFile(index)}
                        className="text-red-500 hover:text-red-700 p-1"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AddTask; 